import {
  Package,
  Snowflake,
  Thermometer,
  Box,
  Sun,
  AlertTriangle,
  Leaf,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { CatalogItem } from '../../types';
import {
  STORAGE_TYPE_LABELS,
  ALLERGEN_LABELS,
  DIETARY_FLAG_LABELS,
} from '../../types';

interface CatalogItemCardProps {
  readonly item: CatalogItem;
  readonly onClick?: () => void;
}

const storageTypeIcons = {
  frozen: Snowflake,
  chilled: Thermometer,
  dry: Box,
  ambient: Sun,
};

const storageTypeColors = {
  frozen: 'text-blue-500',
  chilled: 'text-cyan-500',
  dry: 'text-amber-500',
  ambient: 'text-orange-500',
};

/**
 * Card component displaying a catalog item.
 */
export function CatalogItemCard({
  item, onClick 
}: CatalogItemCardProps) {
  const StorageIcon = storageTypeIcons[item.storageType];
  const storageColor = storageTypeColors[item.storageType];

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg border bg-white p-4 text-left transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-gray-100 p-2">
            <Package className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{item.name}</h3>
            <p className="text-sm text-gray-500">{item.sku}</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <StorageIcon className={cn('h-4 w-4', storageColor)} />
          <span className="text-xs text-gray-500">
            {STORAGE_TYPE_LABELS[item.storageType]}
          </span>
        </div>
      </div>

      {item.description && (
        <p className="mt-2 text-sm text-gray-600 line-clamp-2">
          {item.description}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
          {item.category}
        </span>
        {item.subcategory && (
          <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs text-gray-600">
            {item.subcategory}
          </span>
        )}
      </div>

      {/* Allergens */}
      {item.allergens.length > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <div className="flex flex-wrap gap-1">
            {item.allergens.map((allergen) => (
              <span
                key={allergen}
                className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
              >
                {ALLERGEN_LABELS[allergen] ?? allergen}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Dietary Flags */}
      {item.dietaryFlags.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <Leaf className="h-4 w-4 text-green-500" />
          <div className="flex flex-wrap gap-1">
            {item.dietaryFlags.map((flag) => (
              <span
                key={flag}
                className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
              >
                {DIETARY_FLAG_LABELS[flag] ?? flag}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>
          Unit: {item.unit}
          {item.unitSize && item.unitSize > 1 ? ` (${item.unitSize})` : ''}
        </span>
        {item.shelfLifeDays && <span>Shelf life: {item.shelfLifeDays} days</span>}
      </div>
    </button>
  );
}
